import { animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";
/**
 * Provides a progressively revealed version of `text` while `streamingState`
 * is "streaming", animating a cursor over `delimiter`-separated parts for a
 * smooth reveal; returns the full text once streaming ends or is cancelled.
 * Used by Markdown when `enableAnimation` is set.
 * @summary Hook animating streamed text reveal.
 */
export function useAnimatedText(text, streamingState, animationDurationSeconds, delimiter) {
    const [cursor, setCursor] = useState(0);
    const [startingCursor, setStartingCursor] = useState(0);
    const [prevText, setPrevText] = useState(text);
    const [disableAnimation, setDisableAnimation] = useState(true);
    const controlsRef = useRef(null);
    const streamingStateRef = useRef(streamingState);
    streamingStateRef.current = streamingState;
    // if the new chunks of text have arrived, reset the starting cursor to the current cursor
    if (prevText !== text) {
        setPrevText(text);
        setStartingCursor(cursor);
    }
    useEffect(() => {
        if (streamingStateRef.current !== "streaming") {
            // When streaming ended before this effect ran (e.g. the last text chunk
            // arrived at the same time as streamingState transitioned to "none"),
            // ensure we show the full text instead of getting stuck on a truncated
            // cursor position.
            if (streamingStateRef.current === "none") {
                setDisableAnimation(true);
            }
            return;
        }
        setDisableAnimation(false);
        const textParts = text.split(delimiter);
        // Animates from startingCursor to textParts.length over animationDurationSeconds seconds.
        // Each time new text arrives, the animation restarts from the current cursor.
        // The duration is fixed, so the reveal speed depends on the gap (target - cursor):
        //   - First chunk: small gap (e.g. 29 chars / 1s = ~29 chars/sec) → feels slow/throttled.
        //   - Later chunks: larger gap (e.g. 131 chars / 1s = ~131 chars/sec) → feels smooth
        //     because more chars are crammed into the same duration.
        controlsRef.current = animate(startingCursor, textParts.length, {
            duration: animationDurationSeconds,
            ease: "easeOut",
            // latest is the interpolated cursor position (number of visible characters).
            onUpdate(latest) {
                setCursor(Math.floor(latest));
            },
            onComplete() {
                setDisableAnimation(true);
                controlsRef.current = null;
            },
        });
        return () => {
            var _a;
            (_a = controlsRef.current) === null || _a === void 0 ? void 0 : _a.stop();
        };
    }, [startingCursor, text, delimiter, animationDurationSeconds]);
    useEffect(() => {
        var _a;
        // stop animation if streaming is cancelled.
        if (streamingState === "cancelled") {
            (_a = controlsRef.current) === null || _a === void 0 ? void 0 : _a.stop();
            controlsRef.current = null;
        }
    }, [streamingState]);
    // Return full text immediately if cancelled or none (and animation is finished if streaming before)
    if (streamingState === "cancelled" ||
        (streamingState === "none" && disableAnimation)) {
        return text;
    }
    return text.split(delimiter).slice(0, cursor).join(delimiter);
}
//# sourceMappingURL=useAnimatedText.js.map